/**
 * Holdout + measurement bootstrap of the loader (CONTRACTS §J.2, §J.3).
 *
 * On an eligible page the loader opens the widget session itself (`POST /v1/session` with `visitor_id` + `consent`),
 * gets the experiment arm and only then decides whether to mount the launcher:
 *  - no experiment / `treatment` → mount, emit `widget_eligible` + `widget_impression`;
 *  - `holdout` → nothing is rendered (no launcher, no proactive messages) but `widget_eligible` is emitted under the
 *    same trigger conditions, so the two arms are comparable.
 * The session id is shared with the panel (same localStorage state), so opening the chat resumes this session.
 */
import { clearState, loadState, saveState } from '../shared/storage';
import type { TenantTheme } from '../types/contracts';
import type { AICommerceConfig } from '../types/sdk';
import { ensureShopifyPrivacy, onConsentChange, readConsent, type Consent } from './consent';
import { trackSiteAddToCart } from './siteCart';
import { clearVisitorId, getOrCreateVisitorId } from './visitor';

export interface ExperimentAssignment {
  experiment_id: string;
  arm: 'treatment' | 'holdout';
  bucket: number;
}

/** Additive init options read by the loader (the integrations may pass them; all optional). */
export interface MeasurementConfig extends AICommerceConfig {
  /** Consent already known by the integration (e.g. WP Consent API read server-side). */
  consent?: Partial<Consent>;
  /** `false` disables session bootstrap, holdout and measurement events (launcher mounts as before). */
  measurement?: boolean;
}

export interface MeasurementState {
  visitor_id: string | null;
  session_id: string | null;
  experiment: ExperimentAssignment | null;
  consent: Consent;
}

export interface MeasurementHooks {
  mount(): void;
  isMounted(): boolean;
  /** Tenant of the session opened at page load (theme texts and advisor availability for the launcher). */
  tenant?(tenant: SessionTenant): void;
}

export interface SessionTenant {
  theme?: TenantTheme;
  operator?: { available?: boolean };
}

export interface MeasurementDeps {
  fetchImpl?: typeof fetch;
  /** Max wait for the arm before failing open (mount without measurement). */
  timeoutMs?: number;
}

export interface MeasurementHandle {
  stop(): void;
  /** Resolves when the bootstrap is over (tests). */
  ready: Promise<void>;
}

let state: MeasurementState = { visitor_id: null, session_id: null, experiment: null, consent: { analytics: false, marketing: false, mode: 'unknown' } };

/** Current measurement identity — used by `AICommerce.getAttribution()` and the cart attributes (CONTRACTS §J.4). */
export function getMeasurementState(): MeasurementState {
  return { ...state };
}

const CACHE_PREFIX = 'aicommerce:exp:';

/** Per-tab cache of the assignment of the stored session, so later page views decide without waiting. */
function readCache(tenantKey: string): { session_id: string; experiment: ExperimentAssignment | null } | null {
  try {
    const raw = window.sessionStorage.getItem(CACHE_PREFIX + tenantKey);
    return raw ? (JSON.parse(raw) as { session_id: string; experiment: ExperimentAssignment | null }) : null;
  } catch {
    return null;
  }
}

function writeCache(tenantKey: string, v: { session_id: string; experiment: ExperimentAssignment | null }): void {
  try {
    window.sessionStorage.setItem(CACHE_PREFIX + tenantKey, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}

export function isBot(ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''): boolean {
  const webdriver = typeof navigator !== 'undefined' && (navigator as { webdriver?: boolean }).webdriver === true;
  return webdriver || /bot|crawl|spider|slurp|headless|lighthouse|pingdom|preview|googleother|google-|facebookexternalhit/i.test(ua);
}

export function deviceOf(ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''): 'mobile' | 'desktop' | 'tablet' {
  if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) return 'tablet';
  if (/Mobi|iPhone|iPod|Android|Windows Phone/i.test(ua)) return 'mobile';
  return 'desktop';
}

/** Locale sent with the session: merchant override, else the host page language (the store's, not the browser's). */
export function sessionLocale(cfg: AICommerceConfig): string | undefined {
  if (cfg.locale) return cfg.locale;
  const lang = typeof document !== 'undefined' ? document.documentElement?.lang?.trim() : '';
  return lang ? lang.slice(0, 16) : undefined;
}

function pageOf(cfg: MeasurementConfig): { page_type: string; path: string } {
  const path = typeof location !== 'undefined' ? location.pathname : '/';
  return { page_type: (cfg.context?.page_type ?? 'other').slice(0, 32), path: path.slice(0, 2048) };
}

export function startMeasurement(config: AICommerceConfig, hooks: MeasurementHooks, deps: MeasurementDeps = {}): MeasurementHandle {
  const cfg = config as MeasurementConfig;
  const cleanups: (() => void)[] = [];
  let stopped = false;
  const doFetch: typeof fetch = (...a) => (deps.fetchImpl ?? window.fetch.bind(window))(...a);
  const base = cfg.apiBase.replace(/\/+$/, '');
  const headers = { 'Content-Type': 'application/json', 'X-Tenant-Key': cfg.tenantKey };
  const mount = () => {
    if (!stopped && !hooks.isMounted()) hooks.mount();
  };

  const emit = (type: string, properties: Record<string, unknown> = {}) => {
    if (!state.session_id || stopped) return;
    const page = pageOf(cfg);
    const device = deviceOf();
    const body = {
      session_id: state.session_id,
      type,
      // duplicated in `properties` so they are stored even where the ingestion keeps only properties
      properties: { ...properties, page_type: page.page_type, path: page.path, device, ...(state.experiment ? { experiment_id: state.experiment.experiment_id, arm: state.experiment.arm } : {}) },
      ...(state.visitor_id ? { visitor_id: state.visitor_id } : {}),
      ...(state.experiment ? { experiment: state.experiment } : {}),
      page,
      device,
    };
    try {
      void doFetch(`${base}/v1/events`, { method: 'POST', headers, body: JSON.stringify(body), keepalive: true }).catch(() => undefined);
    } catch {
      /* ignore */
    }
  };

  const resolveIdentity = (): boolean => {
    const consent = readConsent(cfg.consent);
    let visitorId: string | null = null;
    if (consent.analytics) visitorId = getOrCreateVisitorId();
    else if (consent.mode === 'denied') clearVisitorId();
    const changed = visitorId !== state.visitor_id || consent.mode !== state.consent.mode || consent.analytics !== state.consent.analytics || consent.marketing !== state.consent.marketing;
    state = { ...state, consent, visitor_id: visitorId };
    return changed;
  };

  const sessionBody = () =>
    JSON.stringify({
      ...(sessionLocale(cfg) ? { locale: sessionLocale(cfg) } : {}),
      page_url: typeof location !== 'undefined' ? location.href.slice(0, 2048) : undefined,
      ...(state.visitor_id ? { visitor_id: state.visitor_id } : {}),
      consent: state.consent,
    });

  /**
   * Consent given/revoked after the session was opened: re-sends `visitor_id` + `consent` on the same session so the
   * server keeps the id only with consent (revoked → the stored id is dropped, CONTRACTS §J.2). The arm stays sticky.
   */
  const syncIdentity = async () => {
    const sid = state.session_id;
    if (!sid || stopped) return;
    try {
      const r = await doFetch(`${base}/v1/session`, { method: 'POST', headers: { ...headers, 'X-Session-Id': sid }, body: sessionBody(), keepalive: true });
      if (!r.ok || stopped) return;
      const res = (await r.json()) as { session_id?: string; experiment?: ExperimentAssignment };
      if (res.session_id && res.session_id !== state.session_id) {
        saveState(cfg.tenantKey, { session_id: res.session_id });
        clearState(cfg.tenantKey, ['conversation_id']);
        state = { ...state, session_id: res.session_id, experiment: res.experiment ?? state.experiment };
        writeCache(cfg.tenantKey, { session_id: res.session_id, experiment: state.experiment });
      }
    } catch {
      /* best effort: the next page view sends the current consent anyway */
    }
  };

  // A change that lands while `POST /v1/session` is in flight is synced once the session id is known.
  let pendingSync = false;
  let booted = false;
  const onIdentityChange = () => {
    if (!resolveIdentity()) return;
    if (booted) void syncIdentity();
    else pendingSync = true;
  };

  const ready = (async () => {
    if (cfg.measurement === false || isBot()) {
      mount();
      return;
    }
    resolveIdentity();
    // Consent given/revoked later: persist or drop the id (locally and on the session); the arm stays the same (sticky).
    cleanups.push(onConsentChange(onIdentityChange));
    // Shopify themes without a banner never load the Customer Privacy API: load it, then re-read the consent.
    ensureShopifyPrivacy(onIdentityChange);

    const stored = loadState(cfg.tenantKey).session_id;
    const cached = readCache(cfg.tenantKey);
    let decided = false;
    if (stored && cached && cached.session_id === stored) {
      state = { ...state, session_id: stored, experiment: cached.experiment };
      if (!cached.experiment || cached.experiment.arm === 'treatment') mount();
      decided = true;
    }

    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => ctrl?.abort(), deps.timeoutMs ?? 4000);
    let res: { session_id: string; experiment?: ExperimentAssignment; tenant?: SessionTenant } | null = null;
    try {
      // No `customer` here: identity is the panel's job (a token would create a new session on every page view).
      const r = await doFetch(`${base}/v1/session`, {
        method: 'POST',
        headers: stored ? { ...headers, 'X-Session-Id': stored } : headers,
        body: sessionBody(),
        signal: ctrl?.signal,
      });
      if (r.ok) res = (await r.json()) as { session_id: string; experiment?: ExperimentAssignment; tenant?: SessionTenant };
    } catch {
      res = null;
    } finally {
      clearTimeout(timer);
    }
    if (stopped) return;
    if (!res?.session_id) {
      if (!decided) mount(); // fail open: the shop never loses the assistant because measurement failed
      return;
    }

    if (res.session_id !== stored) {
      saveState(cfg.tenantKey, { session_id: res.session_id });
      clearState(cfg.tenantKey, ['conversation_id']); // a stored conversation belongs to the previous session
    }
    if (res.tenant && typeof res.tenant === 'object') hooks.tenant?.(res.tenant);
    const experiment = res.experiment ?? null;
    state = { ...state, session_id: res.session_id, experiment };
    writeCache(cfg.tenantKey, { session_id: res.session_id, experiment });
    booted = true;
    if (pendingSync) {
      pendingSync = false;
      void syncIdentity();
    }
    // Holdout never mounts; a launcher already shown from a stale cache is left in place (no flicker), the unit's
    // arm comes from the server log anyway.
    if (!experiment || experiment.arm === 'treatment') mount();

    emit('widget_eligible');
    if (hooks.isMounted()) emit('widget_impression');
    // storefront add-to-carts (§J.7): the funnel counts them for sessions that chatted, the holdout analysis in both arms
    cleanups.push(trackSiteAddToCart(() => emit('add_to_cart', { source: 'site' })));
  })();

  return {
    ready,
    stop() {
      stopped = true;
      for (const c of cleanups.splice(0)) c();
    },
  };
}
